import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Divider } from "@mui/material";
import { LoadingSpinner } from "pepsico-ds";
import { useDispatch, useSelector } from "react-redux";
import { setOpen } from "../../../slices/navSlice";
import { darkTheme, lightTheme } from "../../../theme/Theme";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "./AppPopover.css";
import useFetchApp from "../../../hooks/useFetchApp";
import LazyImage from "../../../utils/LazyImage";

export default function AppPopover() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "app-popover" : undefined;

  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setOpen(false));
  }, []);
  const { mobileLoading } = useFetchApp();
  const { apps } = useSelector((store) => store.appadmin);
  const popoverRef = useRef(null);
  const isDarkTheme = useSelector((state) => state.theme);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorEl]);
  const handleSelect = (value) => {
    window.open(value.link, "_blank");
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "row", gap: 1, px: 2 }}>
        <IconButton
          size="large"
          aria-label="apps"
          color="inherit"
          aria-describedby={id}
          onClick={handlePopoverOpen}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M4 8H8V4H4V8ZM10 20H14V16H10V20ZM4 20H8V16H4V20ZM4 14H8V10H4V14ZM10 14H14V10H10V14ZM16 4V8H20V4H16ZM10 8H14V4H10V8ZM16 14H20V10H16V14ZM16 20H20V16H16V20Z"
              fill={isDarkTheme ? darkTheme.iconColor1 : lightTheme.iconColor1}
            />
          </svg>
        </IconButton>
      </Box>
      {open && (
        <Box
          ref={popoverRef}
          sx={{
            position: "absolute",
            top: "106%",
            right: "6%",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              width: 320,
              maxHeight: 500,
              backgroundColor: isDarkTheme
                ? darkTheme.backgroundColorLayer1
                : lightTheme.backgroundColorLayer1,
              borderRadius: 2,
              boxShadow: 3,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {mobileLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 400,
                }}
              >
                <LoadingSpinner style={{ width: "30px", height: "30px" }} />
              </Box>
            ) : (
              <>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 20px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: isDarkTheme
                        ? darkTheme.textColor1
                        : lightTheme.textColor1,
                    }}
                  >
                    Applications
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      padding: "4px",
                      color: isDarkTheme
                        ? darkTheme.textColor1
                        : lightTheme.textColor1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Divider
                  sx={{
                    backgroundColor: isDarkTheme
                      ? darkTheme.borderColor
                      : lightTheme.borderColor,
                  }}
                />

                {/* Apps List */}
                <Box
                  sx={{
                    overflowY: "auto",
                    maxHeight: 400,
                    padding: "8px 0",
                  }}
                >
                  {apps?.map((app, index) => (
                    <React.Fragment key={app.id || index}>
                      <Box
                        onClick={() => handleSelect(app)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px 20px",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: isDarkTheme
                              ? darkTheme.backgroundColorLayer2
                              : lightTheme.backgroundColorLayer2,
                          },
                        }}
                      >
                        {/* App Icon */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            marginRight: "12px",
                            flexShrink: 0,
                          }}
                        >
                          <LazyImage
                            width="40px"
                            height="40px"
                            url={app.cdnUrl || app.image}
                            borderRadius={8}
                          />
                        </Box>

                        {/* App Name */}
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "14px",
                            fontWeight: 400,
                            color: isDarkTheme
                              ? darkTheme.textColor1
                              : lightTheme.textColor1,
                          }}
                        >
                          {app.title}
                        </Typography>

                        {/* External Link Icon */}
                        <IconButton
                          size="small"
                          sx={{
                            padding: "4px",
                            color: "#005CBC",
                            marginLeft: "8px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(app);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {index < apps.length - 1 && (
                        <Divider
                          sx={{
                            marginLeft: "20px",
                            marginRight: "20px",
                            backgroundColor: isDarkTheme
                              ? darkTheme.borderColor
                              : lightTheme.borderColor,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
